import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../ApiProvider';
import { unwrap } from '../client';
import type { components } from '../schema.gen';
import { queryKeys } from './keys';

export type ProductType = components['schemas']['ProductType'];
export type ProductTypeSummary = components['schemas']['ProductTypeSummary'];
export type ProductField = components['schemas']['ProductField'];
export type ProductFieldInput = components['schemas']['ProductFieldInput'];
export type FieldFormat = components['schemas']['FieldFormat'];
export type SourceColumns = components['schemas']['SourceColumns'];
export type ProductPreview = components['schemas']['ProductPreview'];

/** Every product type, by name. */
export function useProducts() {
  const api = useApiClient();
  return useQuery({
    queryKey: queryKeys.products(),
    queryFn: async () => (await unwrap(api.GET('/api/admin/products'))).items,
  });
}

/** A product type and its saved fields, in order. */
export function useProduct(id: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: queryKeys.product(id),
    queryFn: () =>
      unwrap(api.GET('/api/admin/products/{product_id}', { params: { path: { product_id: id } } })),
  });
}

/** The latest upload's column headings, for the "not in the latest upload" warnings. */
export function useSourceColumns(id: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: queryKeys.productSourceColumns(id),
    queryFn: () =>
      unwrap(
        api.GET('/api/admin/products/{product_id}/source-columns', {
          params: { path: { product_id: id } },
        }),
      ),
  });
}

/** A sample line of this type from its latest upload, for the preview. */
export function useProductPreview(id: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: queryKeys.productPreview(id),
    queryFn: () =>
      unwrap(
        api.GET('/api/admin/products/{product_id}/preview', {
          params: { path: { product_id: id } },
        }),
      ),
  });
}

/** Adds a product type, then refreshes the list. */
export function useCreateProduct() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => unwrap(api.POST('/api/admin/products', { body: { name } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.products() }),
  });
}

/** Saves a type's whole ordered field list, then refreshes product types and pay views. */
export function useSaveFields() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fields }: { id: string; fields: ProductFieldInput[] }) =>
      unwrap(
        api.PUT('/api/admin/products/{product_id}/fields', {
          params: { path: { product_id: id } },
          body: { fields },
        }),
      ),
    onSuccess: (product) => {
      queryClient.setQueryData(queryKeys.product(product.id), product);
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.products() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.pay() }),
      ]);
    },
  });
}

/** Renames a product type (and its line items), then refreshes product types and pay views. */
export function useRenameProduct() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      unwrap(
        api.PATCH('/api/admin/products/{product_id}', {
          params: { path: { product_id: id } },
          body: { name },
        }),
      ),
    onSuccess: (product) => {
      queryClient.setQueryData(queryKeys.product(product.id), product);
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.products() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.pay() }),
      ]);
    },
  });
}
